import {
  crouch,
  E,
  flick,
  graffitiLine,
  hold,
  N,
  ride,
  roll,
  stepOff,
  tap,
  targetsLine,
  up,
  W,
  type DemoLine,
  type TagWall,
  type TargetRow,
} from "./script";

// street's demo lines, played in this order (`DEMO_LINES` in `demo.ts`).

/** The spot the targets line shoots from, and the row (the map file's `targets`). */
const TARGETS: TargetRow = {
  range: { x: -23, z: -27 },
  row: [
    [-25.3, -35.7],
    [-28, -35.7],
    [-30.8, -34.8],
    [-29.1, -30.5],
    [-29.8, -28.8],
  ],
};

// Built on call so the hill site drops them (#229; see `parkV2Lines`).
export const streetLines = (): DemoLine[] => [
  {
    id: "street-six",
    name: "Kickflip the Big Six",
    mapId: "street",
    // Across the office entrance and off the top of its six-stair (#115).
    start: { x: 0, z: -36, yaw: 0, speed: 7 },
    segments: [roll(0.2), crouch(0.3), roll(0.02), tap("flip"), roll(1)],
    lands: ["Kickflip", "Big Six"],
  },
  {
    id: "street-rail",
    name: "Down the six-stair handrail, Crooked, kickflip off",
    mapId: "street",
    start: { x: 2, z: -36.5, yaw: 0, speed: 7 },
    segments: [
      roll(0.3),
      crouch(0.3),
      roll(0.02),
      tap("grind"),
      ride(0.2),
      tap("grind", -1, 1),
      ride(0.1),
      tap("ollie"),
      roll(0.02),
      tap("flip"),
      roll(0.9),
    ],
    lands: ["50-50", "Six Stair Rail", "Crooked", "Kickflip"],
  },
  {
    id: "street-hubba",
    name: "Boardslide the hubba, kickflip off",
    mapId: "street",
    // Beside the six-stair (#152): onto the granite ledge sloping down its west side.
    start: { x: -3.4, z: -36.5, yaw: 0, speed: 7 },
    // A lower pop and a later grind tap since the hubba grew (#167), so it
    // locks on in the air rather than landing on its top first.
    segments: [
      roll(0.3),
      crouch(0.2),
      roll(0.1),
      tap("grind"),
      ride(0.15),
      tap("grind", -1, 0),
      ride(0.15),
      tap("ollie"),
      roll(0.02),
      tap("flip"),
      roll(1),
    ],
    lands: ["Boardslide", "The Hubba", "Kickflip"],
  },
  {
    id: "street-picnic",
    name: "Coffee shop picnic tables: 50-50 one, hop to the next, kickflip off",
    mapId: "street",
    // Along the benches outside the coffee shop (#167), west from the graffiti wall.
    start: { x: -23.5, z: 33.5, yaw: W, speed: 7 },
    segments: [
      roll(0.25),
      crouch(0.2),
      roll(0.05),
      tap("grind"),
      ride(0.1),
      crouch(0.15),
      roll(0.05),
      tap("grind"),
      ride(0.2),
      tap("ollie"),
      roll(0.02),
      tap("flip"),
      roll(1.2),
    ],
    lands: ["50-50", "Kickflip"],
  },
  {
    id: "street-3block",
    name: "Kickflip the 3-block",
    mapId: "street",
    start: { x: 12, z: -36, yaw: 0, speed: 7 },
    segments: [roll(0.2), crouch(0.3), roll(0.02), tap("flip"), roll(1)],
    lands: ["Kickflip", "The 3-Block"],
  },
  {
    id: "street-dock",
    name: "Heelflip the Dock Gap, 50-50 the dock edge",
    mapId: "street",
    // Along the entrance plaza, over the gap onto the loading dock, then
    // along its front edge and off the end.
    start: { x: 9, z: -33, yaw: E, speed: 7 },
    segments: [
      roll(0.3),
      crouch(0.3),
      roll(0.02),
      tap("flip", 1),
      roll(1.2),
      roll(0.5, 1),
      roll(0.6),
      { dur: 1, x: 0, y: -1 },
    ],
    lands: ["Heelflip", "Dock Gap"],
  },
  {
    id: "street-sidewalk",
    name: "The sidewalk rail, for C-O-M-B-O",
    mapId: "street",
    // Boardslide and Crooked switch-ups along the railing by the curb,
    // kickflip off into a manual, then ollie out.
    start: { x: -35, z: -10.6, yaw: E, speed: 9 },
    segments: [
      crouch(0.5),
      roll(0.12),
      tap("grind"),
      ride(0.4),
      tap("grind", -1),
      ride(0.4),
      tap("grind", -1, 1),
      ride(0.4),
      tap("ollie"),
      roll(0.02),
      tap("flip"),
      roll(0.5),
      ...flick([0, 1], [0, -1]),
      ride(0.3),
      tap("ollie"),
      roll(0.6),
      { dur: 1.5, x: 0, y: -1 },
    ],
    lands: [
      "Sidewalk Rail",
      "50-50",
      "Boardslide",
      "Crooked",
      "Kickflip",
      "Manual",
    ],
  },
  {
    id: "street-ledge",
    name: "Granite ledge, bench, heelflip off",
    mapId: "street",
    // 50-50 the whole ledge, roll off, pop onto the bench and heelflip off
    // its end, then roll on east and brake past the dock (started east
    // of the long ledge since #130 angled it).
    start: { x: -14.6, z: -16.9, yaw: E, speed: 9.5 },
    segments: [
      crouch(0.25),
      roll(0.05),
      tap("grind"),
      ride(1),
      roll(0.28),
      crouch(0.2),
      roll(0.02),
      tap("grind"),
      ride(0.3),
      tap("ollie"),
      roll(0.02),
      tap("flip", 1),
      roll(1.1),
      { dur: 1.2, x: 0, y: -1 },
    ],
    lands: ["50-50", "Heelflip"],
  },
  {
    id: "street-bank",
    name: "Up the bank, Method over the entrance plaza",
    mapId: "street",
    // East up the bank at the plaza's west end (turned to run along the
    // block by #130), a Method for the Secret Tape, then roll along it and brake.
    start: { x: -31, z: -34.5, yaw: E, speed: 9 },
    segments: [
      roll(0.85),
      crouch(0.3),
      roll(0.02),
      hold("grab", 0.4),
      roll(1),
      { dur: 1.5, x: 0, y: -1 },
    ],
    lands: ["Method"],
  },
  {
    id: "street-south",
    name: "Indy off the bank across the street",
    mapId: "street",
    // Across the street from the office steps, up the bank against the
    // south buildings (#130), crouched for height.
    start: { x: -2, z: 26, yaw: 0, speed: 9 },
    segments: [
      crouch(0.95),
      roll(0.15),
      tap("grab", -1),
      hold("grab", 0.6),
      roll(1.2),
      { dur: 1.5, x: 0, y: -1 },
    ],
    lands: ["Indy"],
  },
  {
    id: "street-west",
    name: "The long granite ledge, kickflip off",
    mapId: "street",
    // West along the long ledge toward the west bank (angled toward the
    // 3-block's ledge by #130), kickflip out before its end and brake.
    start: { x: -14.7, z: -13.8, yaw: W - 0.28, speed: 7 },
    segments: [
      crouch(0.5),
      roll(0.12),
      tap("grind"),
      ride(0.6),
      tap("ollie"),
      roll(0.02),
      tap("flip"),
      roll(0.6),
      { dur: 1.5, x: 0, y: -1 },
    ],
    lands: ["50-50", "Kickflip"],
  },
  {
    id: "street-manual",
    name: "Manual across the plaza, hardflip out",
    mapId: "street",
    // A long manual along the open plaza under the entrance steps, then
    // flip out of it and brake.
    start: { x: -11, z: -18.2, yaw: E, speed: 8 },
    segments: [
      roll(0.2),
      ...flick([0, 1], [0, -1]),
      ride(2.4),
      tap("ollie"),
      roll(0.02),
      tap("flip", -1, 1),
      roll(0.8),
      roll(0.6, 1),
      { dur: 1.2, x: 0, y: -1 },
    ],
    lands: ["Manual", "Hardflip"],
  },
  {
    id: "street-shop-four",
    name: "Kickflip the Shop Row four",
    mapId: "street",
    // Across the shops' terrace on the south plaza (#152) and off its
    // four-stair, north toward the street.
    start: { x: 43, z: 35, yaw: N, speed: 7 },
    segments: [roll(0.2), crouch(0.3), roll(0.02), tap("flip"), roll(1)],
    lands: ["Kickflip", "Shop Row Four"],
  },
  {
    id: "street-shop-rail",
    name: "Down the Shop Row handrail, kickflip off",
    mapId: "street",
    start: { x: 48, z: 35, yaw: N, speed: 7 },
    segments: [
      roll(0.3),
      crouch(0.3),
      roll(0.02),
      tap("grind"),
      ride(0.3),
      tap("ollie"),
      roll(0.02),
      tap("flip"),
      roll(0.9),
    ],
    lands: ["50-50", "Shop Row Rail", "Kickflip"],
  },
  {
    id: "street-bmx-endo",
    name: "On the BMX: an endo along the plaza, Endo Nac-Nac then One-Foot Endo",
    mapId: "street",
    // Off the board, L1 round the pack to the BMX and on (#141), then up
    // on the front wheel (down, up) along the plaza under the entrance
    // steps: grab + right swings a leg over the back for an Endo Nac-Nac,
    // grab + left takes the left foot off for a One-Foot Endo (#138).
    start: { x: -14, z: -19.5, yaw: E, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      roll(0.3),
      tap("spinL"),
      roll(0.3),
      ...stepOff(),
      roll(1.2),
      ...flick([0, -1], [0, 1]),
      ride(0.3),
      tap("grab", 1),
      hold("grab", 0.6),
      ride(0.3),
      tap("grab", -1),
      hold("grab", 0.5),
      ride(0.5),
      // Hop out: the plaza runs on past where the old end bank used to stop it (#152).
      tap("ollie"),
      roll(1.5),
    ],
    lands: ["Nose Manual", "Endo Nac-Nac", "One-Foot Endo"],
    ride: "bmx",
  },
  {
    id: "street-bmx-xup",
    name: "On the BMX: an X-Up Manual along the plaza",
    mapId: "street",
    // Off the board onto the BMX (#141), then a manual (up, down) along
    // the plaza: grab + up crosses the bars round for an X-Up Manual
    // (#138), and an ollie hops out of it.
    start: { x: -14, z: -19.5, yaw: E, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      roll(0.3),
      tap("spinL"),
      roll(0.3),
      ...stepOff(),
      roll(1.2),
      ...flick([0, 1], [0, -1]),
      ride(0.3),
      tap("grab", 0, 1),
      hold("grab", 0.7),
      ride(0.4),
      tap("ollie"),
      roll(1.5),
    ],
    lands: ["Manual", "X-Up Manual"],
    ride: "bmx",
  },
  {
    id: "street-climb",
    name: "Step off, climb the plaza's front wall, Caveman off it",
    mapId: "street",
    // Ledge climbing (#19; the old Rooftop's water tower line, moved here
    // by #172): off the board, run at the plaza's 1.8 m front wall west of
    // the steps and jump; you catch the edge and the stick held up climbs
    // you over. A turn west and a jump off the plaza's end with both
    // triggers drops the board under you: a Caveman.
    start: { x: -16, z: -22, yaw: N, speed: 5 },
    segments: [
      roll(0.3),
      ...stepOff(),
      up(1.0),
      { dur: 0.05, x: 0, y: 1, hold: ["ollie"] },
      up(1.4),
      up(0.4),
      { dur: 0.45, x: -1, y: 0 },
      up(0.3),
      { dur: 0.05, x: 0, y: 1, hold: ["ollie"] },
      { dur: 0.1, x: 0, y: 1 },
      ...stepOff(),
      roll(0.3),
      { dur: 1.2, x: 0, y: -1 },
    ],
    lands: ["Caveman"],
  },
];

/** The "Hit 5 targets" line (#137), played after every map's other lines. */
export const STREET_TARGETS: DemoLine = targetsLine(
  "street",
  TARGETS,
  "Paint the five targets down the west end",
  E,
);

/** The graffiti on the south shopfronts (the map file's `graffiti`), and where to stand to wash each tag. */
const GRAFFITI: TagWall = {
  face: 0,
  stands: [
    [-25, 35.8],
    [-19.5, 35.8],
    [-14, 35.8],
  ],
};

/** The "Wash off 3 graffiti tags" challenge (#151). */
export const STREET_GRAFFITI: DemoLine = graffitiLine(
  "street",
  GRAFFITI,
  "Power wash the graffiti off the south shopfronts",
);
